import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { REFERRALAPIURL } from '../config';

/**
 * CompanyContext
 * Central place to store and access the active company branding (logo + name) for the referral app.
 * Provides helper setters so any component (e.g. after verifying a GUID) can update the branding
 * without prop-drilling through layout components.
 */

const DEFAULT_LOGO = './FieldworkerIcon.png';

const CompanyContext = createContext({
	companyName: 'FieldWorker',
	companyLogo: DEFAULT_LOGO,
	companyId: null,
	loading: false,
	setCompanyBranding: () => {},
	resetCompanyBranding: () => {},
	setLoading: () => {},
});

export const CompanyProvider = ({ children, initialName = 'FieldWorker', initialLogo = DEFAULT_LOGO }) => {
	const [companyName, setCompanyName] = useState(initialName);
	const [companyLogo, setCompanyLogo] = useState(initialLogo);
	const [companyId, setCompanyId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [hydrated, setHydrated] = useState(false);
	const convertingRef = useRef(false);
	const fetchedRef = useRef(null);
	const lastRouteIdRef = useRef(null);

	const blobToImageDataUrl = useCallback((blob) => new Promise((resolve, reject) => {
		try {
			let fallbackMime = 'image/png';
			const mime = (blob && blob.type && blob.type.startsWith('image/')) ? blob.type : fallbackMime;
			const reader = new FileReader();
			reader.onloadend = () => {
				let result = reader.result; // may not be image mime
				if (typeof result === 'string' && !result.startsWith('data:image')) {
					const idx = result.indexOf('base64,');
					if (idx !== -1) {
						const b64 = result.substring(idx + 7);
						result = `data:${mime};base64,${b64}`;
					}
				}
				resolve(result);
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		} catch (e) { reject(e); }
	}), []);

	const setCompanyBranding = useCallback(({ name, logo, id }) => {
		if (name) setCompanyName(name);
		if (logo) setCompanyLogo(logo);
		if (id !== undefined) {
			const coerced = Number(id);
			if (!Number.isNaN(coerced)) {
				setCompanyId(coerced);
				lastRouteIdRef.current = coerced;
				fetchedRef.current = null; // allow refetch when id changes via browser nav
			}
		}
	}, []);

	const resetCompanyBranding = useCallback(() => {
		setCompanyName('FieldWorker');
		setCompanyLogo(DEFAULT_LOGO);
		setCompanyId(null);
		fetchedRef.current = null;
		lastRouteIdRef.current = null;
		try { localStorage.removeItem('fwReferralBranding'); } catch(_){}
	}, []);

	const value = useMemo(() => ({
		companyName,
		companyLogo,
		companyId,
		loading,
		hydrated,
		setCompanyBranding,
		resetCompanyBranding,
		setLoading,
	}), [companyName, companyLogo, companyId, loading, hydrated, setCompanyBranding, resetCompanyBranding]);


	// Hydrate from cache on first load
	useEffect(() => {
		if (hydrated) return;
		try {
			const cached = localStorage.getItem('fwReferralBranding');
			if (cached) {
				const { id, name, logo } = JSON.parse(cached);
				if (id) setCompanyId(id);
				if (name) setCompanyName(name);
				if (logo) setCompanyLogo(logo);
				lastRouteIdRef.current = id;
			}
		} catch(_){}
		setHydrated(true);
	}, [hydrated]);

	// Fetch logo when needed (first time or after browser nav changing guid)
	useEffect(() => {
		if (!companyId) return;
		const isDefault = !companyLogo || companyLogo === DEFAULT_LOGO;
		if (!isDefault && fetchedRef.current === companyId) return; // already have custom logo
		if (fetchedRef.current === companyId && isDefault) return; // already attempted and still default
		let canceled = false;
		(async () => {
			try {
				const resp = await axios.get(`${REFERRALAPIURL}/company/logo/${companyId}`);
				console.log('Fetched company logo for id', companyId, resp);
				
				if (canceled) return;
				if (resp?.data?.logo?.data) {
					const arrayBufferToBase64 = (buffer) => {
						let binary = '';
						let bytes = Array.from(new Uint8Array(buffer));
						bytes.forEach(b => binary += String.fromCharCode(b));
						return window.btoa(binary);
					};
					const base64Flag = `data:${resp.data.type || 'image/png'};base64,`;
					const imageStr = arrayBufferToBase64(resp.data.logo.data);
					const dataUri = base64Flag + imageStr;
					setCompanyLogo(dataUri);
					try { localStorage.setItem('fwReferralBranding', JSON.stringify({ id: companyId, name: companyName, logo: dataUri })); } catch(_){}
				} else {
					// No logo found; keep default
					if (fetchedRef.current !== companyId) {
						setCompanyLogo(DEFAULT_LOGO);
						try { localStorage.setItem('fwReferralBranding', JSON.stringify({ id: companyId, name: companyName, logo: DEFAULT_LOGO })); } catch(_){}
					}
				}
			} catch(_) {
				/* ignore */
			} finally {
				fetchedRef.current = companyId;
			}
		})();
		return () => { canceled = true; };
	}, [companyId, companyLogo, companyName]);

	// Listen for browser navigation (popstate) that changes guid in URL and trigger reset/refetch
	useEffect(() => {
		const handler = () => {
			const path = window.location.pathname.replace(/^\//,'');
			if (!path) return; // root
			// assume path is guid; force reapply to trigger fetch if different
			if (lastRouteIdRef.current !== companyId) {
				fetchedRef.current = null; // allow refetch
			}
		};
		window.addEventListener('popstate', handler);
		return () => window.removeEventListener('popstate', handler);
	}, [companyId]);

	// (was object URL cleanup; no longer needed since using data URIs)

	return (
		<CompanyContext.Provider value={value}>
			{children}
		</CompanyContext.Provider>
	);
};

export const useCompany = () => useContext(CompanyContext);

export default CompanyContext;
